import { EmptyState } from '@/components/EmptyState'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import ReactECharts from 'echarts-for-react'
import { useAtomValue } from 'jotai'
import { ChartSpline } from 'lucide-react'
import { forwardRef } from 'react'
import { MetricSelector } from '../components/MetricSelector'
import { CATEGORY_COLOURS, FALLBACK_COLOUR } from '../constants/riskProfile.constants'
import { useChartDisplayRange } from '../hooks/useChartDisplayRange'
import { METRIC_OPTIONS, useProfileAnalysisFilters } from '../hooks/useProfileAnalysisFilters'
import type { RiskProfileMetricOption, RiskProfileResponse } from '../types/riskProfile.types'
import { formatRiskProfileValue } from '../utils/riskProfile.utils'
import { ChartDisplayRange } from './ChartDisplayRange'
import { mapRiskProfileResponseToAreaRanking } from './RiskProfileChartData'

const TOP_N_HAZARDS = 10

export interface RiskByHazardsChartProps {
  data: RiskProfileResponse
  metricOption: RiskProfileMetricOption
}

const RiskByHazardsChart = forwardRef<ReactECharts, RiskByHazardsChartProps>(
  ({ data, metricOption }, ref) => {
    const { showOption, setShowOption, topN } = useChartDisplayRange(data.group_totals.length)
    const currencySymbol = useAtomValue(currencySymbolAtom)
    const defaultUnit =
      metricOption.metric === 'damage_cost' || metricOption.metric === 'replacement_cost'
        ? currencySymbol
        : null
    const chartData = mapRiskProfileResponseToAreaRanking(data, {
      metricOption,
      topN,
    })

    const { setMetricOption } = useProfileAnalysisFilters()

    if (chartData.areaLabels.length === 0) {
      return (
        <EmptyState
          title=""
          description={
            metricOption.metric === 'quantity'
              ? "Quantity can't be shown across elements with different units. Choose a single element type, or use Quantity (%) metric instead."
              : 'No data is available for the selected filters. Try adjusting your selections to view results.'
          }
          showButton={false}
          icon={<ChartSpline className="size-8 stroke-rexBlue" />}
          action={
            <MetricSelector
              value={metricOption.value}
              onValueChange={setMetricOption}
              label=""
              options={METRIC_OPTIONS}
            />
          }
        />
      )
    }

    const labels = chartData.areaLabels.map((label) => label)

    // Get pre-calculated totals from group_totals, matching the order of areaLabels
    const labelToGroupKey = new Map(data.groups.map((group) => [group.label, group.key]))
    const groupKeyToTotal = new Map(
      data.group_totals.map((total) => [
        total.group_key,
        { value: total.value, unit: total.unit, percent_value: total.percent_value },
      ]),
    )

    const totals = labels.map((label) => {
      const groupKey = labelToGroupKey.get(label)
      const total = groupKey ? groupKeyToTotal.get(groupKey) : undefined
      return total
        ? {
            value: total.value,
            unit: total.unit ?? defaultUnit,
            percent_value: total.percent_value,
          }
        : { value: 0, unit: defaultUnit, percent_value: 0 }
    })

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (value: number) => formatRiskProfileValue(value, metricOption, defaultUnit),
      },
      legend: {
        top: 0,
        left: 0,
        show: false,
      },
      grid: {
        left: 0,
        right: 40,
        top: 16,
        bottom: 32,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatRiskProfileValue(value, metricOption, defaultUnit),
        },
        splitNumber: 4,
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        axisLabel: {
          align: 'left',
          width: 160,
          margin: 160,
          overflow: 'truncate',
        },
      },
      series: chartData.series.map((series, seriesIndex) => ({
        name: series.name,
        type: 'bar',
        stack: 'total',
        data: series.values,
        itemStyle: { color: CATEGORY_COLOURS[series.name] ?? FALLBACK_COLOUR },
        label:
          seriesIndex === chartData.series.length - 1
            ? {
                show: true,
                position: 'right',
                formatter: (params: { dataIndex: number }) =>
                  formatRiskProfileValue(
                    metricOption.type == 'total'
                      ? (totals[params.dataIndex].value ?? 0)
                      : (totals[params.dataIndex].percent_value ?? 0),
                    metricOption,
                    totals[params.dataIndex].unit,
                  ),
                color: '#000',
                fontWeight: 'bold',
              }
            : undefined,
      })),
    }

    return (
      <>
        <ChartDisplayRange
          shown={chartData.areaLabels.length}
          total={data.group_totals.length}
          value={showOption}
          onChange={setShowOption}
        />

        <ReactECharts
          ref={ref}
          option={option}
          notMerge
          style={{ height: Math.max(280, chartData.areaLabels.length * 36 + 80) }}
        />
      </>
    )
  },
)

RiskByHazardsChart.displayName = 'RiskByHazardsChart'

export default RiskByHazardsChart
